import { Language } from '@eneco/common/application';
import { $OrderProductTypeDto } from '@eneco/dc-be/client/schemas.gen';
import {
  postEnecoBeXapiSiteApiV1OrdersInitiate,
  putEnecoBeXapiSiteApiV1OrdersByOrderIdBasket,
} from '@eneco/dc-be/client/services.gen';
import { SimpleOrderBasketLineItemDto } from '@eneco/dc-be/client/types.gen';

import { languageToCustomerLanguageDto } from '../../../../utils/language';

const ORDER_PRODUCT_TYPE_ENUM = [...$OrderProductTypeDto.enum] as const;

const ORDER_PRODUCT_TYPE_OPTIONS = Object.fromEntries(ORDER_PRODUCT_TYPE_ENUM.map(v => [v, v])) as {
  [K in (typeof ORDER_PRODUCT_TYPE_ENUM)[number]]: K;
};

interface InitiateOrderParams {
  language: Language;
  priceSimulationId: string | undefined;
  accountNumber: string;
  electricityProductTechnicalName?: string;
  gasProductTechnicalName?: string;
  serviceProposal?: string;
  boilerMaintenance?: string;
  promoId?: string;
}

export const initiateOrder = async (params: InitiateOrderParams): Promise<string> => {
  const {
    accountNumber,
    language,
    priceSimulationId,
    boilerMaintenance,
    electricityProductTechnicalName,
    gasProductTechnicalName,
    serviceProposal,
  } = params;

  const items: SimpleOrderBasketLineItemDto[] = [
    ...(serviceProposal && serviceProposal !== 'none'
      ? [
          {
            technicalProductName: serviceProposal,
            orderProductType: ORDER_PRODUCT_TYPE_OPTIONS.NonCommodity,
          },
        ]
      : []),
    ...(boilerMaintenance && boilerMaintenance !== 'none'
      ? [
          {
            technicalProductName: boilerMaintenance,
            orderProductType: ORDER_PRODUCT_TYPE_OPTIONS.NonCommodity,
          },
        ]
      : []),
    ...(electricityProductTechnicalName
      ? [
          {
            technicalProductName: electricityProductTechnicalName,
            orderProductType: ORDER_PRODUCT_TYPE_OPTIONS.Commodity,
          },
        ]
      : []),
    ...(gasProductTechnicalName
      ? [
          {
            technicalProductName: gasProductTechnicalName,
            orderProductType: ORDER_PRODUCT_TYPE_OPTIONS.Commodity,
          },
        ]
      : []),
  ];

  const { response, data } = await postEnecoBeXapiSiteApiV1OrdersInitiate({
    body: {
      basket: {
        items,
      },
      language: languageToCustomerLanguageDto(language),
      priceSimulationId,
      accountNumber,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to initiate order management flow');
  }

  const orderId = data?.data?.orderId;
  if (!orderId) {
    throw new Error('Order ID is missing');
  }

  return orderId;
};

export const updateOrderBasket = async ({
  orderId,
  priceSimulationId,
  items,
}: {
  orderId: string;
  priceSimulationId: string | undefined;
  items: SimpleOrderBasketLineItemDto[];
}): Promise<void> => {
  const { response } = await putEnecoBeXapiSiteApiV1OrdersByOrderIdBasket({
    path: { orderId },
    body: { basket: { items }, priceSimulationId },
  });
  if (!response.ok) {
    throw new Error('Failed to update order basket');
  }
};
